import React, { useState } from 'react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { Button } from '@/atoms/Button'
import { Input } from '@/atoms/Input'
import { AsLabel, CaptionXS, LabelXS } from '@/atoms/Text'
import { LOGIN_REDIRECT_URL, LOGIN_STARTED_ORIGIN, LOGIN_USER_EMAIL } from '@/constants/cookies'
import { ANGEL_STUDIOS_EMAIL_GROUP_ID } from '@/constants/email'
import { paths } from '@/constants/paths'
import { AuthApi } from '@/services/Auth/AuthService'
import { useCaptureOrderEmail } from '@/services/EmailCaptureService'
import { isValidEmail } from '@/utils/StripeFormValidator'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { getAttribution } from '@/views/GuildStripeCheckoutSessionView/GuildEmbeddedStripeCheckoutSession'

export type LoginOrigin = 'home' | 'shared' | 'episode' | 'mercy-ships-multi-claim'

interface GuildEmailInputProps {
  hasUser?: boolean
  loginOrigin?: LoginOrigin
  className?: string
  inputContainerClassName?: string
  buttonTextOverride?: string
}

export const GuildEmailInput: React.FC<GuildEmailInputProps> = ({
  hasUser = false,
  loginOrigin,
  className,
  inputContainerClassName,
  buttonTextOverride,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const router = useSafeRouter()
  const captureOrderEmail = useCaptureOrderEmail()
  const returnToUrl = getReturnToLoginUrl(loginOrigin)
  const { loginUrl, trackLoginStarted } = useLoginUrl(returnToUrl)
  const authService = new AuthApi()

  const buttonText = buttonTextOverride
    ? buttonTextOverride
    : loginOrigin && (loginOrigin === 'shared' || loginOrigin === 'episode')
    ? t('watchFree', 'Watch Free')
    : t('getStarted', 'Get Started')

  const checkIfUserExists = async (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    setCookie(LOGIN_STARTED_ORIGIN, loginOrigin)
    setCookie(LOGIN_REDIRECT_URL, returnToUrl)
    track('Guild Email Input Signup Attempted', { email })
    setError('')
    if (hasUser) {
      router.push(returnToUrl)
      return
    }

    if (!isValidEmail(email)) {
      setError(t('invalidEmail', 'Please enter a valid email address.'))
      return
    }
    event.preventDefault()

    const { projectSlugs } = getAttribution()
    captureOrderEmail({
      position: 'guild-auth-flow',
      subscriptionGroupId: ANGEL_STUDIOS_EMAIL_GROUP_ID,
      email: email,
      projectSlug: projectSlugs ?? '',
    })
      .then(() => {
        track('Guild Signup Email Submitted', { email })
      })
      .catch((e) => {
        logger().error('An error occurred while attempting to capture order email', e)
        track('Guild Signup Email Submission Error', { email })
      })

    const userCheck = await fetch(`/api/authByApi/checkUser?email=${encodeURIComponent(email)}`)
    const userCheckJson = await userCheck.json()

    setCookie(LOGIN_USER_EMAIL, email)
    if (userCheckJson.exists) {
      router.push(loginUrl)
      trackLoginStarted('GuildEmailInput')
      return
    } else {
      await authService.sendVerificationCode(email)
      router.push(paths.guild.signup.createAccount)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      checkIfUserExists(event)
    }
  }

  return (
    <div onKeyDown={handleKeyDown} className="w-full">
      <div className={classNames('flex w-full items-center gap-4', className)}>
        <div className={classNames('w-full')}>
          <Input
            label=""
            onChange={(e) => {
              setEmail(e.target.value)
            }}
            value={email}
            placeholder={t('email', 'Email Address')}
            containerClassName={classNames('!mt-0', inputContainerClassName)}
            inputClasses="ring-transparent outline-none shadow-none bg-core-gray-900 border border-solid border-core-gray-800 text-white p-4 max-h-[18px] rounded-[10px] lg:min-w-[326px] placeholder:text-core-gray-400 w-full md:w-fit"
            labelClassName="photon-label-sm font-whitney font-medium"
          />
          {error && (
            <div className="h-4">
              <CaptionXS role="alert" className="mx-auto">
                <span className="text-error-500">{error}</span>
              </CaptionXS>
            </div>
          )}
        </div>

        <Button
          onClick={checkIfUserExists}
          variant="black"
          className={classNames(
            'min-w-[101px] !max-w-[101px] lg:min-w-[129px] lg:max-w-[129px] rounded-lg !border-[#FFA41C] !bg-[#FFA41C] !max-h-[34px]',
            className,
          )}
        >
          <LabelXS
            as={AsLabel}
            color={'black'}
            weight="bold"
            className="flex h-full min-w-[101px] cursor-pointer flex-row items-center justify-center gap-2 !text-xs lg:!text-sm"
          >
            {buttonText}
          </LabelXS>
        </Button>
      </div>
    </div>
  )
}

function getReturnToLoginUrl(loginOrigin?: LoginOrigin) {
  if (loginOrigin === 'home') {
    return paths.guild.pricing
  } else if (loginOrigin === 'mercy-ships-multi-claim') {
    return '/m/shared/mercy-ships'
  } else if (loginOrigin === 'episode') {
    return typeof window !== 'undefined' ? window.location.href : paths.watch.index
  } else {
    return paths.guild.pricing
  }
}
